import { AlertErrorCircle } from '@/assets/icons'
import { useContext, useEffect, useState } from 'react'
import { useSendInviteMutation } from '../apis/org-admin.api'
import { CreateUserInviteRequest, InviteEmailStatus } from '../schemas'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { buildInviteLink } from '../utils/build-invite-link'
import { AuthContext } from '@/context/auth-context'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/browser'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectDisplayCostPerSeatInCents, selectHasForeverPercentDiscount } from '@/account/store/selectors'
import { RootState } from '@/store/store'
import { StripeSubscriptionInterval, StripeSubscriptionStatus } from '@/account/store/schemas'
import { formatCentsToDollarsCompact } from '@/util/money'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AccountActions } from '@/account/store/slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

type InviteMemberDialogProps = {
  visible: boolean
  onInviteSuccess: () => void
  onClose: () => void
}

/**
 * Invite Member Dialog
 *
 * Dialog for inviting a team member to the organization - displays per-seat pricing so users are aware of the
 * cost added to their subscription if the invite is accepted.
 *
 * Pricing behaviour:
 * - If there is no discount, the normal per-seat price is shown
 * - If there is a forever-percent discount, the discount is applied to the per-seat price shown
 * - For any other discount (amount-off, non-forever percent, etc.), the per-seat price is shown without the discount,
 *   and a footnote is shown to indicate that the price does not reflect the active discount
 */
export default function InviteMemberDialog(props: InviteMemberDialogProps) {
  const { visible, onInviteSuccess, onClose } = props
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()

  // Local state
  const [error, setError] = useState<string | null>(null)
  const [emailNotice, setEmailNotice] = useState<string | null>(null)
  const [undeliveredInvite, setUndeliveredInvite] = useState<{ id: string; email: string } | null>(null)
  const [emailInput, setEmailInput] = useState<string>('')

  // RTK-Query Mutation
  const [sendInvite, { isLoading }] = useSendInviteMutation()

  // User Context
  const { userAccountData } = useContext(AuthContext)
  const organizationId = userAccountData?.orgData?.id
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo

  // Redux state
  const isAccountDataLoading = useAppSelector((s: RootState) => s.accountState.loading)
  const subscriptionStatus = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionStatus)
  const subscriptionInterval = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionInterval)
  const hasDiscount = useAppSelector((s: RootState) => s.accountState.hasDiscount)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)
  const displayCostPerSeatInCents = useAppSelector(selectDisplayCostPerSeatInCents)

  // Inferred values
  const isInTrial = subscriptionStatus === StripeSubscriptionStatus.TRIALING
  const intervalAdjective = subscriptionInterval === StripeSubscriptionInterval.MONTH ? 'monthly' : 'annual'
  const showFootnote = hasDiscount && !hasForeverPercentDiscount
  const isPricingDataAvailable = displayCostPerSeatInCents != null && subscriptionInterval != null

  // Build the invite member seat pricing sentence
  const sentencePrefix = 'Add a team member for '
  const priceText = displayCostPerSeatInCents != null ? `${formatCentsToDollarsCompact(displayCostPerSeatInCents)} per ${subscriptionInterval}` : ''
  const sentenceSuffix = isInTrial
    ? ` Charges won't apply until your trial ends. Accepted seats will appear on your first ${intervalAdjective} invoice.`
    : " You'll be billed when they accept, prorated for the time left in your billing cycle."

  // Map of error detail to user-friendly error message
  const inviteErrorMessages: Record<string, string> = {
    'Invite already exists':
      'This user already has a pending invitation. In the "Invites" tab, you can click "Resend invitation" in the menu next to their invitation.',
    'User already in organization': 'This user is already a member of your organization.',
    default: 'There was an error creating the invitation. Please try again.',
  }

  function getUserFriendlyErrorMessage(errorDetail: string | undefined): string {
    return errorDetail && inviteErrorMessages[errorDetail] ? inviteErrorMessages[errorDetail] : inviteErrorMessages.default
  }

  async function handleSubmit() {
    if (!isLoading) {
      setError(null)
      const trimmedEmail = emailInput.trim()

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setError('Please enter a valid email address.')
        return
      }

      try {
        // Send the invite
        const inviteArgs: CreateUserInviteRequest = {
          invite: {
            organization_id: organizationId || '',
            invitee_email: trimmedEmail,
          },
        }

        // Use unwrap to handle the promise and catch errors
        const response = await sendInvite(inviteArgs).unwrap()

        // Clear input and refresh the invites list - the invite exists even if its email did not go out
        setEmailInput('')
        onInviteSuccess()
        // Track the invite-created action regardless of email outcome (the invite record exists
        // either way), but carry the email status so we can see how often the email never lands
        trackEvent(AnalyticsEvent.OrganizationInviteSent, {
          organizationId: organizationId || '',
          organizationInviteToEmail: trimmedEmail,
          emailStatus: response.email_status,
        })

        // The invite persists even when its email fails or is filtered - keep the dialog
        // open and say so inline, offering the invite link as the delivery path (ENG-6983)
        if (response.email_status === InviteEmailStatus.FAILED) {
          setEmailNotice(`The invite for ${trimmedEmail} was created, but the invite email could not be sent. Share the invite link instead:`)
          setUndeliveredInvite({ id: response.invite.id, email: trimmedEmail })
          return
        }
        if (response.email_status === InviteEmailStatus.REDIRECTED_TEST_INBOX) {
          setEmailNotice(
            `The invite for ${trimmedEmail} was created, but this environment only emails @paxton.ai or allowlisted addresses, so no email was sent to them. Share the invite link instead:`
          )
          setUndeliveredInvite({ id: response.invite.id, email: trimmedEmail })
          return
        }
        if (response.email_status !== InviteEmailStatus.SENT) {
          // Unknown/future status - stay neutral rather than claiming delivery
          setEmailNotice(
            `The invite for ${trimmedEmail} was created, but we couldn't confirm the invite email was delivered. Share the invite link if it doesn't arrive:`
          )
          setUndeliveredInvite({ id: response.invite.id, email: trimmedEmail })
          return
        }

        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Invite sent to ${trimmedEmail}`, durationMs: kToastDurationMediumMs }))
        onClose()
      } catch (error) {
        let errorMessage = inviteErrorMessages.default

        // Handle FetchBaseQueryError from RTK Query
        if ((error as FetchBaseQueryError).data && typeof (error as FetchBaseQueryError).data === 'object') {
          const errData = (error as FetchBaseQueryError).data as { detail?: string }

          // Check if the error is a 409 Conflict and get specific message if available
          if ((error as FetchBaseQueryError).status === 409) {
            errorMessage = getUserFriendlyErrorMessage(errData.detail)
          }
        } else {
          Sentry.captureException(new Error(`Unable to send invitation to email ${trimmedEmail}`), {
            extra: { error: JSON.stringify(error), email: trimmedEmail, organizationId: organizationId },
          })
        }

        setError(errorMessage)
      }
    }
  }

  async function handleCopyInviteLink() {
    if (!undeliveredInvite) return
    const inviteLink = buildInviteLink(window.location.origin, undeliveredInvite.id, undeliveredInvite.email)
    await navigator.clipboard.writeText(inviteLink)
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Invite link copied', durationMs: kToastDurationMediumMs }))
  }

  // Handle close
  function handleClose() {
    setError(null)
    setEmailNotice(null)
    setUndeliveredInvite(null)
    setEmailInput('')
    onClose()
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEmailInput(e.target.value)
    setError(null)
    setEmailNotice(null)
    setUndeliveredInvite(null)
  }

  // Force refresh of payment details to ensure we have the most up to date data to display pricing
  useEffect(() => {
    if (canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: true }))
    }
  }, [canViewSubscriptionInfo, dispatch])

  return (
    <Modal open={visible} onOpenChange={(open) => !open && handleClose()}>
      <ModalContent
        title="Invite member"
        width="lg"
        height="max-sm"
        aria-describedby={isPricingDataAvailable || isAccountDataLoading ? 'invite-member-description' : undefined}
        footer={
          <ModalFooterWithActions
            tertiaryText={
              showFootnote ? (
                <p className="text-sm text-brand-neutral-600">
                  <span className="text-brand-800">*</span> Price shown before promotions
                </p>
              ) : undefined
            }
            secondaryAction={{ label: 'Cancel', onClick: handleClose }}
            primaryAction={{ label: 'Send invitation', onClick: handleSubmit, loading: isLoading, disabled: isLoading || !emailInput.trim() }}
          />
        }
      >
        {/* Pricing sentence */}
        {(isPricingDataAvailable || isAccountDataLoading) && (
          <div id="invite-member-description" className="mb-6">
            {!isPricingDataAvailable && isAccountDataLoading && (
              <div className="flex items-center justify-center">
                <CircularProgressContinuousSized size={16} thickness={6} />
              </div>
            )}
            {isPricingDataAvailable && (
              <p>
                {sentencePrefix}
                <span className={clsx('font-bold', showFootnote && 'text-brand-800')}>{priceText}</span>
                {showFootnote && <span className="text-brand-800">*</span>}
                {/* Omit the period after the price text if we're showing the price text with an asterisk - this is intentional.
                 * It's a common practice in UX microcopy to make the text look cleaner and easier to scan. */}
                {!showFootnote && '.'}
                {sentenceSuffix}
              </p>
            )}
          </div>
        )}

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          {/* Email input */}
          <input
            className={clsx(
              'w-full rounded border-0 py-2.5 ring-1 ring-inset focus:ring-inset focus:outline-none',
              error ? 'ring-red-400 focus:ring-red-400' : 'ring-brand-neutral-400 focus:ring-brand-400'
            )}
            type="email"
            autoFocus={true}
            placeholder="Email address"
            value={emailInput}
            onChange={handleInputChange}
            required
          />

          {/* Error message */}
          {error && (
            <p className="p-2 mt-2 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
              <AlertErrorCircle className="mt-[1px] self-start shrink-0 w-3.5 h-3.5" />
              {error}
            </p>
          )}

          {/* Invite created but the email did not reach the invitee - offer the link directly */}
          {emailNotice && (
            <div className="mt-2">
              <InlineMessage variant="warning">
                {emailNotice}
                {undeliveredInvite && (
                  <button type="button" onClick={handleCopyInviteLink} className="ml-1 font-semibold underline hover:text-yellow-900">
                    Copy invite link
                  </button>
                )}
              </InlineMessage>
            </div>
          )}
        </form>
      </ModalContent>
    </Modal>
  )
}
